/* eslint-disable @next/next/next-script-for-ga */
import { PICO_SITE_NAME } from '@/constants'
import { mapToProductSchema } from '@/constants'
import { useNotification } from '@/containers'
import type { Category, Post, Product } from '@/types'
import { getCookie } from 'cookies-next'
import { NextSeo } from 'next-seo'
import type { MetaTag } from 'next-seo/lib/types'
import Head from 'next/head'
import Script from 'next/script'
import { useRouter } from 'next/router'
interface ogType {
  title?: string
  description?: string
  url?: string
  images?: [
    {
      url?: string | ''
      alt?: string
    }
  ]
  site_name?: string
}
type IMetaProps = {
  title: string
  description: string
  canonical?: string
  imageUrl?: string
  noIndex?: boolean
  noFollow?: boolean
  keywords?: string[]
  imageAlt?: string
  additionalMetaTags?: MetaTag[]
  openGraph?: ogType
  dataSchema?: Product | Post | Category
  typeSchema?: string
}

const Meta = (props: IMetaProps) => {
  const router = useRouter()
  const { systemConfig } = useNotification()
  // Canonical giữ `page` (> 1) vì mỗi trang phân trang render tập sản phẩm
  // khác nhau (đo prod: ?page=2, ?page=3 ra 25 sản phẩm khác trang 1); bỏ mọi
  // tham số khác (sort, filter, utm...) vì chỉ đổi thứ tự/lọc cùng nội dung.
  function toCanonicalUrl(url: string) {
    // new URL('') ném TypeError: Invalid URL. Trang không truyền canonical sẽ
    // rơi về NEXT_PUBLIC_URL_WEBSITE, mà lúc build trên CI env này hay thiếu
    // hoặc rỗng -> prerender /404, /maintenance, /register chết và fail cả
    // `next build` ("Export encountered errors on following paths"). Thiếu
    // canonical thì chỉ nên bỏ thẻ đi, tuyệt đối không được làm hỏng build.
    if (!url) return ''
    try {
      const parsedUrl = new URL(url)
      const baseUrl = parsedUrl.origin + parsedUrl.pathname
      const page = Number(parsedUrl.searchParams.get('page'))
      return Number.isInteger(page) && page > 1
        ? `${baseUrl}?page=${page}`
        : baseUrl
    } catch {
      return ''
    }
  }
  const currentRegion = getCookie('sale-region')
  let schemaObject = {}
  if (props.dataSchema && props.typeSchema == 'product') {
    schemaObject = mapToProductSchema(props.dataSchema)
  }
  const isRobotEnabled = process.env.NEXT_PUBLIC_ROBOT === 'true'
  return (
    <>
      <Head>
        <meta charSet='UTF-8' key='charset' />
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0'
          key='viewport'
          user-scalable='no'
        />
        <meta name='apple-mobile-web-app-capable' content='yes' />
        <meta name='mobile-web-app-capable' content='yes' />
        <meta name="format-detection" content="telephone=no, email=no, address=no, date=no" />
        <link
          rel='apple-touch-icon'
          href={`${router.basePath}/apple-touch-icon.png`}
          key='apple'
        />
        <meta
          name='facebook-domain-verification'
          content='5cxkiti6unyvkx119huhzve7nfw29z'
        />

        <link
          rel='icon'
          type='image/png'
          sizes='16x16'
          href={systemConfig?.companyLogoFavicon}
          key='icon32'
        />
        <link
          rel='icon'
          href={systemConfig?.companyLogoFavicon}
          key='favicon'
        />
        <script
          id='gtm'
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GA_TRACKING_ID}')`,
          }}
        />
        <script
          id='fb-pixel'
          dangerouslySetInnerHTML={{
            // Chỉ là stub xếp hàng lệnh fbq(...) - không tải mạng. Thư viện
            // fbevents.js nạp bằng <Script strategy='lazyOnload'> (sau sự kiện
            // load) để không tranh băng thông với LCP. Xem CHANGELOG-perf.md.
            __html: `!function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[]}(window, document);
      fbq('init', ${
        currentRegion == 'MN1'
          ? process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID_MN || 498920596301042
          : ''
      });
      fbq('track', 'PageView');`,
          }}
        />
        {props.dataSchema && props.typeSchema == 'product' && (
          <script
            type='application/ld+json'
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(schemaObject, null, 2),
            }}
            key='product-jsonld'
          />
        )}
        <noscript
          dangerouslySetInnerHTML={{
            __html: `<img height="1" width="1" style="display:none"
      src="https://www.facebook.com/tr?id=${
        currentRegion == 'MN1'
          ? process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID_MN || 498920596301042
          : ''
      }&ev=PageView&noscript=1" />`,
          }}
        />
      </Head>
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${process.env.NEXT_PUBLIC_GA_TRACKING_ID}`}
          height='0'
          width='0'
          style={{ display: 'none', visibility: 'hidden' }}
        ></iframe>
      </noscript>
      {currentRegion == 'MN1' && (
        <Script
          id='fb-pixel-lib'
          src='https://connect.facebook.net/en_US/fbevents.js'
          strategy='lazyOnload'
        />
      )}
      <NextSeo
        title={props.title}
        description={props.description}
        canonical={toCanonicalUrl(
          props.canonical || process.env.NEXT_PUBLIC_URL_WEBSITE || ''
        )}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: props.keywords
              ? props.keywords.join(',')
              : `${systemConfig?.websiteBrandName || PICO_SITE_NAME}, chuyên gia điện máy, điện tử, điện lạnh, điều hòa, tủ lạnh, máy giặt, đồ gia dụng, tivi, khuyến mại, giao hàng nhanh`,
          },
        ]}
        noindex={isRobotEnabled ? props.noIndex : true}
        nofollow={isRobotEnabled ? props.noFollow : true}
        openGraph={{
          title: props.title,
          description: props.description,
          url: props.canonical ? toCanonicalUrl(props.canonical) : props.canonical,
          images: [
            {
              url: props?.imageUrl || '',
              width: 800,
              height: 600,
              alt: props?.imageAlt,
            },
          ],
          site_name: PICO_SITE_NAME,
          locale: 'vi_VN',
        }}
      />
    </>
  )
}

export { Meta }

